import { useState } from "react";

import { useSnackbarController, useTranslation } from "@firecms/core";
import { Button, CircularProgress, RefreshIcon, Tooltip } from "@firecms/ui";

import { collectionStringsFor } from "../i18n/collection-strings";
import {
    describeOutcomes,
    rebuildTargets,
    triggerRebuilds,
    type RebuildTarget,
} from "../lib/rebuild";

/**
 * A manual rebuild button per environment, in the collection toolbar.
 *
 * **One button per environment, never one for both.** The whole point of
 * develop is rebuilding it without touching production — a single button would
 * make reviewing a draft and publishing to the live site the same gesture.
 *
 * It exists for the edits an editor cannot make through this UI: a record
 * changed directly in the Firebase console, a `firestore.rules` deploy, or a
 * save whose automatic rebuild failed. Saving through the form already
 * rebuilds on its own (`onSaveRebuild`), so this is the recovery path, not the
 * normal one.
 *
 * Renders nothing when no hook is configured, which is the honest signal for a
 * CMS build that has none — a dead button would be worse.
 */
export function RebuildActions() {
    const snackbar = useSnackbarController();
    const { i18n } = useTranslation();
    const strings = collectionStringsFor(i18n.language).rebuild;
    const [running, setRunning] = useState<string | null>(null);

    if (rebuildTargets.length === 0) return null;

    const run = async (target: RebuildTarget) => {
        setRunning(target.key);
        try {
            const described = describeOutcomes(
                await triggerRebuilds([target]),
                strings,
            );
            if (described) snackbar.open(described);
        } finally {
            setRunning(null);
        }
    };

    return (
        <>
            {rebuildTargets.map((target) => (
                <Tooltip
                    key={target.key}
                    title={strings.tooltip(target.label)}
                >
                    <Button
                        variant="text"
                        size="small"
                        color={
                            target.key === "production" ? "error" : "primary"
                        }
                        disabled={running !== null}
                        onClick={() => run(target)}
                        startIcon={
                            running === target.key ? (
                                <CircularProgress size="smallest" />
                            ) : (
                                <RefreshIcon size="small" />
                            )
                        }
                    >
                        {strings.button(target.label)}
                    </Button>
                </Tooltip>
            ))}
        </>
    );
}
